// `/account/settings`, WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON: THE ACCOUNT, AS THE APP'S ACCOUNT SCREEN HAS IT (ACC-3b).
//
// ACCOUNTS_PLAN §3 G, in the app's order (AccountManagementView.swift) with the website's additions the plan names:
//   G1   "Account", and A2's line under it (the app's own, :38, :96);
//   G2/G3 the account's email, "Change email" — re-entry first (C6), then our server sends E4 to the new address and
//        nothing changes until its link is opened (verify-before-update, the seam's `changeEmail`);
//   G4   "Change password" — the current password (which is C6's re-entry), then the new one; E6 goes out (`changePassword`);
//   G10  "Ways to sign in": Apple and email-and-password, each ✓ or "Add …" — "Add Apple" is the seam's
//        `continueWithApple` on an account (C10, C11 where it refuses), "Add a password" links one under the account's
//        own email after an Apple re-entry (C6);
//   G5   the reminders switch, G6 the news-and-offers box (A9's words) — EM-1's ONE STORE for an account's email
//        choices: each answer is `contactSet` on the `web-settings` surface with the hash of the words shown (G5 and A9
//        are issued for it, functions/src/v2/email/wording.ts), and the page shows what `contactGet` says is on file, never
//        what a tab kept. With EMAIL_CONTACT_ENABLED off the server answers 400 and the two rows are not shown.
//        G5 IS HIDDEN UNTIL THE REMINDER JOB (EM-3) EXISTS (R189 change 3, Tyler 2026-10-07, "yes to all four wording
//        changes"): it renders, and a reminders answer can be sent, only while `WEBSITE_REMINDERS_LIVE` — the website's one
//        switch for "a reminder exists" (routes/steps/detailsRules.ts, F-EM-9) — is true, which today it is not. Its
//        words stay held (`ACCOUNT_COPY`) for EM-3;
//   G7   "Sign out", its footer, and the app's own question "Sign out of PROppeal?" before it (:46, :49, :99) — then F4;
//   G8   "Sign out on all devices": the server revokes every sign-in (`signOutEverywhere`), then this browser signs out;
//   G9   "Delete account", K12 under it, to `/delete-account`, where the re-entry and the deletion are.
// A browser signed out of any account is sent to "Sign in" (B), and comes back here.
//
// EVERY WORD IS AN APPROVED ONE (`heldCopy.ts`, `ACCOUNT_COPY`, "Sentences approved", 2026-09-30), the app's (`afterFilingCopy.ts`
// for its "Cancel" and "Apple Relay"), or C-row refusal copy (`refusalCopy.ts`). No sentence is composed here.

import { useId, useState, type ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import type { ContactState } from "@contract";
import type { AuthRefusal } from "../../services";
import { signedIn } from "../../app/accounts";
import { textSha256 } from "../../app/consentHash";
import { useSession } from "../../app/session";
import { WEBSITE_REMINDERS_LIVE } from "../steps/detailsRules";
import { useCall } from "../../app/useCall";
import { afterText } from "../../components/afterFilingCopy";
import { heldTemplate, heldText, type AccountCopyId } from "../../components/heldCopy";
import { Refusal } from "../../components/Refusal";
import { AccountForm, AppleButton, AuthError, EmailField, HeroTile, PasswordField, Say, SubmitButton } from "./parts";
import { looksLikeEmail } from "./refusalCopy";

/** "Apple Relay" for Apple's hide-my-email address, as the app's Account screen shows it (AccountManagementView.swift:33). */
function shownEmail(email: string | null): string {
  if (email === null) return "";
  return email.includes("privaterelay.appleid.com") ? afterText("account.email.appleRelay") : email;
}

export function Settings() {
  const session = useSession();
  const [signedOutHere, setSignedOutHere] = useState(false);
  if (signedOutHere) return <SignedOutDone />;
  if (!signedIn(session.auth)) {
    return <Navigate to="/signin" replace state={{ from: "/account/settings" }} />;
  }
  return <SettingsPage onSignedOut={() => setSignedOutHere(true)} />;
}

/** F4, after "Sign out" or "Sign out on all devices", with the way back in. */
function SignedOutDone() {
  return (
    <section className="account-page" data-account-screen="signed-out-done">
      <HeroTile />
      <div className="note good" role="status">
        <Say id="account.signedOut.done" className="copy" />
      </div>
      <p className="account-links">
        <Link className="btn primary block" to="/signin" state={{ from: "/account/settings" }} data-held-copy="account.signIn.button">
          {heldText("account.signIn.button")}
        </Link>
      </p>
    </section>
  );
}

function Row({ label, children, rowId }: { label: AccountCopyId; children: ReactNode; rowId: string }) {
  return (
    <section className="settings-row card" data-settings-row={rowId}>
      <Say id={label} as="h2" className="settings-label" />
      {children}
    </section>
  );
}

function SettingsPage({ onSignedOut }: { onSignedOut: () => void }) {
  const session = useSession();
  // G5/G6 (EM-1's store). 400 is the server's EMAIL_CONTACT_ENABLED off: no rows, as before EM-1. Anything else, its sentence.
  const contact = useCall(() => session.transport.v2Credits.contactGet(), [session.transport, session.revision]);
  const contactOff = contact.status === "failed" && contact.failure.kind === "v2Error" && contact.failure.status === 400;
  const ways = session.auth.linkedProviders;
  const headingId = useId();

  return (
    <section className="account-page settings" aria-labelledby={headingId} data-account-screen="settings">
      <HeroTile />
      <h1 id={headingId} data-held-copy="account.settings.heading">{heldText("account.settings.heading")}</h1>
      <Say id="account.settings.tied" className="account-lead" />

      <EmailRow />
      {ways.includes("password") && <PasswordRow />}
      <WaysRow />

      {contact.status === "failed" && !contactOff && <Refusal failure={contact.failure} context="Your account" />}
      {contact.status === "ok" && <EmailAnswers standing={contact.response.body.contact} />}

      <SignOutRows onSignedOut={onSignedOut} />

      <section className="settings-row card danger-zone" data-settings-row="delete">
        <Link className="btn ghost block" to="/delete-account" data-held-copy="account.settings.delete">
          {heldText("account.settings.delete")}
        </Link>
        <Say id="account.k.deleteFooter" className="field-foot" />
      </section>
    </section>
  );
}

/** C6's re-entry, in the account's own way: its password, or Apple's window. Calls `then` once it has succeeded. */
function useReentry(): {
  reauthWith: "password" | "apple" | null;
  run: (password: string) => Promise<AuthRefusal | null>;
} {
  const session = useSession();
  const reauthWith = session.auth.reauthenticateWith;
  return {
    reauthWith,
    run: async (password) => {
      const result = reauthWith === "apple"
        ? await session.auth.reauthenticate({ apple: true })
        : await session.auth.reauthenticate({ password });
      return result.ok ? null : result.refusal;
    },
  };
}

/** C6's sentence and the field (or Apple's button) it asks for. */
function Reentry({ password, setPassword, onApple, busy }: {
  password: string;
  setPassword: (v: string) => void;
  onApple: () => void;
  busy: boolean;
}) {
  const session = useSession();
  if (session.auth.reauthenticateWith === "apple") {
    return (
      <div className="reentry">
        <Say id="account.error.reauthApple" className="field-foot" />
        <AppleButton copyId="account.signIn.apple" onClick={onApple} disabled={false} busy={busy} />
      </div>
    );
  }
  return (
    <div className="reentry">
      <Say id="account.error.reauthPassword" className="field-foot" />
      <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />
    </div>
  );
}

/** G2, G3. */
function EmailRow() {
  const session = useSession();
  const reentry = useReentry();
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async () => {
    if (working) return;
    setRefusal(null);
    if (!looksLikeEmail(newEmail)) return setRefusal({ kind: "notAnEmail" });
    setWorking(true);
    try {
      const reauth = await reentry.run(password);
      if (reauth !== null) return setRefusal(reauth);
      const changed = await session.auth.changeEmail(newEmail.trim());
      if (!changed.ok) return setRefusal(changed.refusal);
      setSentTo(newEmail.trim());
      setOpen(false);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Row label="account.settings.email" rowId="email">
      <p className="settings-value" data-account-email>{shownEmail(session.auth.email)}</p>
      {sentTo !== null && (
        <div className="note good" role="status" data-held-copy="account.settings.changeEmailSent">
          {heldTemplate("account.settings.changeEmailSent", { newEmail: sentTo })}
        </div>
      )}
      {!open ? (
        <button type="button" className="btn ghost" onClick={() => setOpen(true)} data-held-copy="account.settings.changeEmail">
          {heldText("account.settings.changeEmail")}
        </button>
      ) : (
        <AccountForm onSubmit={() => void submit()}>
          <Say id="account.settings.changeEmailHelp" className="field-foot" />
          <EmailField value={newEmail} onChange={setNewEmail} invalid={refusal?.kind === "notAnEmail"} autoFocus />
          <Reentry password={password} setPassword={setPassword} onApple={() => void submit()} busy={working} />
          <AuthError refusal={refusal} reauthWith={reentry.reauthWith} />
          <SubmitButton id="account.settings.changeEmail" busy={working} />
          <button type="button" className="btn ghost block" onClick={() => setOpen(false)} data-app-copy="account.cancel">
            {afterText("account.cancel")}
          </button>
        </AccountForm>
      )}
    </Row>
  );
}

/** G4: the current password is the re-entry (C6); then the new one. */
function PasswordRow() {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (working) return;
    setRefusal(null);
    if (next.length < 8) return setRefusal({ kind: "passwordTooShort" });
    setWorking(true);
    try {
      const reauth = await session.auth.reauthenticate({ password: current });
      if (!reauth.ok) return setRefusal(reauth.refusal);
      const changed = await session.auth.changePassword(next);
      if (!changed.ok) return setRefusal(changed.refusal);
      setDone(true);
      setOpen(false);
      setCurrent("");
      setNext("");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Row label="account.settings.password" rowId="password">
      {done && (
        <div className="note good" role="status">
          <Say id="account.settings.passwordChanged" className="copy" />
        </div>
      )}
      {!open ? (
        <button type="button" className="btn ghost" onClick={() => { setDone(false); setOpen(true); }} data-held-copy="account.settings.changePassword">
          {heldText("account.settings.changePassword")}
        </button>
      ) : (
        <AccountForm onSubmit={() => void submit()}>
          <LabelledPassword label="account.settings.currentPassword" value={current} onChange={setCurrent} autoComplete="current-password" />
          <LabelledPassword label="account.settings.newPassword" value={next} onChange={setNext} autoComplete="new-password" hint />
          <AuthError refusal={refusal} reauthWith="password" />
          <SubmitButton id="account.settings.changePassword" busy={working} />
          <button type="button" className="btn ghost block" onClick={() => setOpen(false)} data-app-copy="account.cancel">
            {afterText("account.cancel")}
          </button>
        </AccountForm>
      )}
    </Row>
  );
}

/** A password field under a G4 label ("Current password", "New password"). */
function LabelledPassword({ label, value, onChange, autoComplete, hint }: {
  label: "account.settings.currentPassword" | "account.settings.newPassword";
  value: string;
  onChange: (v: string) => void;
  autoComplete: "new-password" | "current-password";
  hint?: boolean;
}) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="field account-field">
      <label htmlFor={id} data-held-copy={label}>{heldText(label)}</label>
      <div className="password-row">
        <input
          id={id}
          name={autoComplete === "new-password" ? "new-password" : "password"}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
        <button type="button" className="reveal" aria-controls={id} data-held-copy={shown ? "account.field.hide" : "account.field.show"} onClick={() => setShown((s) => !s)}>
          {heldText(shown ? "account.field.hide" : "account.field.show")}
        </button>
      </div>
      {hint === true && <p className="field-foot" data-held-copy="account.field.passwordHint">{heldText("account.field.passwordHint")}</p>}
    </div>
  );
}

/** G10. */
function WaysRow() {
  const session = useSession();
  const ways = session.auth.linkedProviders;
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState<"apple" | "password" | null>(null);
  const [adding, setAdding] = useState(false);
  const [password, setPassword] = useState("");

  const addApple = async () => {
    if (working !== null) return;
    setRefusal(null);
    setWorking("apple");
    try {
      const result = await session.auth.continueWithApple();
      if (!result.ok) return setRefusal(result.refusal);
      session.refresh();
    } finally {
      setWorking(null);
    }
  };

  const addPassword = async () => {
    if (working !== null) return;
    setRefusal(null);
    if (password.length < 8) return setRefusal({ kind: "passwordTooShort" });
    setWorking("password");
    try {
      // C6 for an Apple account: Apple's window again, then the link.
      const reauth = await session.auth.reauthenticate({ apple: true });
      if (!reauth.ok) return setRefusal(reauth.refusal);
      const added = await session.auth.addPassword(password);
      if (!added.ok) return setRefusal(added.refusal);
      setAdding(false);
      setPassword("");
      session.refresh();
    } finally {
      setWorking(null);
    }
  };

  return (
    <Row label="account.settings.ways" rowId="ways">
      <ul className="ways">
        <li data-way="apple" data-linked={ways.includes("apple")}>
          {ways.includes("apple") ? (
            <Say id="account.settings.ways.apple" as="span" className="linked" />
          ) : (
            <button type="button" className="btn ghost" disabled={working !== null} onClick={() => void addApple()} data-held-copy="account.settings.ways.addApple">
              {heldText("account.settings.ways.addApple")}
            </button>
          )}
        </li>
        <li data-way="password" data-linked={ways.includes("password")}>
          {ways.includes("password") ? (
            <Say id="account.settings.ways.password" as="span" className="linked" />
          ) : !adding ? (
            <button type="button" className="btn ghost" onClick={() => setAdding(true)} data-held-copy="account.settings.ways.addPassword">
              {heldText("account.settings.ways.addPassword")}
            </button>
          ) : (
            <AccountForm onSubmit={() => void addPassword()}>
              {/* Which address this password goes with — Apple's relay address, when that is the account's (approved
                  2026-10-01, `ADD_PASSWORD_COPY`). */}
              {session.auth.email !== null && (
                <p className="field-foot" data-held-copy="account.settings.ways.addPasswordEmail">
                  {heldTemplate("account.settings.ways.addPasswordEmail", { email: session.auth.email })}
                </p>
              )}
              <PasswordField value={password} onChange={setPassword} autoComplete="new-password" hint />
              <Say id="account.error.reauthApple" className="field-foot" />
              <SubmitButton id="account.settings.ways.addPassword" busy={working === "password"} />
            </AccountForm>
          )}
        </li>
      </ul>
      <AuthError refusal={refusal} reauthWith="apple" />
      <Say id="account.settings.ways.anywhere" className="field-foot" />
    </Row>
  );
}

/** G5 and G6: each change is the server's record before it is the page's (the page re-reads `contactGet`). G5 only while
 *  `WEBSITE_REMINDERS_LIVE` (R189): until then its row — the only caller of `answer("reminders", …)` — is not rendered. */
function EmailAnswers({ standing }: { standing: ContactState }) {
  const session = useSession();
  const [failure, setFailure] = useState<Parameters<typeof Refusal>[0]["failure"] | null>(null);
  const [working, setWorking] = useState<"reminders" | "marketing" | null>(null);

  const answer = async (kind: "reminders" | "marketing", on: boolean) => {
    if (working !== null) return;
    setFailure(null);
    const words = kind === "reminders" ? heldText("account.settings.reminders") : heldText("account.create.marketing");
    const hash = await textSha256(words);
    if (hash === null) return;
    setWorking(kind);
    try {
      const result = await session.transport.v2Credits.contactSet({
        surface: "web-settings",
        clientTimestampMs: Date.now(),
        [kind]: { on, textSha256: hash },
      });
      if (!result.ok) return setFailure(result);
      session.refresh();
    } finally {
      setWorking(null);
    }
  };

  return (
    <>
      {WEBSITE_REMINDERS_LIVE && <section className="settings-row card" data-settings-row="reminders">
        <div className="switch-row">
          <span>
            <Say id="account.settings.reminders" as="span" className="settings-label strong" />
            <Say id="account.settings.remindersHelp" className="field-foot" />
          </span>
          <button
            type="button"
            role="switch"
            className={`switch${standing.reminders ? " on" : ""}`}
            aria-checked={standing.reminders}
            aria-label={heldText("account.settings.reminders")}
            aria-busy={working === "reminders" || undefined}
            disabled={working !== null}
            data-reminders={standing.reminders ? "on" : "off"}
            onClick={() => void answer("reminders", !standing.reminders)}
          >
            <span className="knob" aria-hidden="true" />
          </button>
        </div>
      </section>}
      <section className="settings-row card" data-settings-row="marketing">
        <label className="consent-box">
          <input
            type="checkbox"
            checked={standing.marketing}
            disabled={working !== null}
            data-marketing={standing.marketing ? "on" : "off"}
            onChange={(e) => void answer("marketing", e.currentTarget.checked)}
          />
          <Say id="account.create.marketing" as="span" />
        </label>
      </section>
      {failure !== null && <Refusal failure={failure} context="Your account" />}
    </>
  );
}

/** G7 and G8. */
function SignOutRows({ onSignedOut }: { onSignedOut: () => void }) {
  const session = useSession();
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState<"here" | "everywhere" | null>(null);
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [failure, setFailure] = useState<Parameters<typeof Refusal>[0]["failure"] | null>(null);

  /** Sign this browser out, start a fresh look-around (ACCOUNTS_PLAN §2.4: sign out, clear the tab's appeal id). */
  const endHere = async (): Promise<boolean> => {
    const result = await session.auth.signOut();
    if (!result.ok && !result.signedOut) {
      setRefusal(result.refusal);
      return false;
    }
    session.forgetAppeal();
    session.refresh();
    onSignedOut();
    return true;
  };

  const signOut = async () => {
    if (working !== null) return;
    setRefusal(null);
    setWorking("here");
    try {
      await endHere();
    } finally {
      setWorking(null);
    }
  };

  const signOutEverywhere = async () => {
    if (working !== null) return;
    setRefusal(null);
    setFailure(null);
    setWorking("everywhere");
    try {
      const revoked = await session.transport.v2Credits.signOutEverywhere();
      if (!revoked.ok) return setFailure(revoked);
      await endHere();
    } finally {
      setWorking(null);
    }
  };

  return (
    <section className="settings-row card" data-settings-row="sign-out">
      {!confirming ? (
        <button type="button" className="btn ghost block" onClick={() => setConfirming(true)} data-held-copy="account.settings.signOut">
          {heldText("account.settings.signOut")}
        </button>
      ) : (
        <div className="confirm" role="alertdialog" aria-labelledby="sign-out-confirm" data-sign-out-confirm>
          <h3 id="sign-out-confirm" data-held-copy="account.settings.signOutConfirm">{heldText("account.settings.signOutConfirm")}</h3>
          <div className="copyrow">
            <button type="button" className="btn ghost" onClick={() => setConfirming(false)} disabled={working !== null} data-app-copy="account.cancel">
              {afterText("account.cancel")}
            </button>
            <button type="button" className="btn primary" onClick={() => void signOut()} aria-busy={working === "here" || undefined} disabled={working !== null} data-held-copy="account.settings.signOut">
              {heldText("account.settings.signOut")}
            </button>
          </div>
        </div>
      )}
      <Say id="account.settings.signOutFooter" className="field-foot" />
      <button
        type="button"
        className="btn ghost block"
        onClick={() => void signOutEverywhere()}
        aria-busy={working === "everywhere" || undefined}
        disabled={working !== null}
        data-held-copy="account.settings.signOutEverywhere"
      >
        {heldText("account.settings.signOutEverywhere")}
      </button>
      <Say id="account.settings.signOutEverywhereHelp" className="field-foot" />
      <AuthError refusal={refusal} />
      {failure !== null && <Refusal failure={failure} context="Signing out" />}
    </section>
  );
}
